import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import type { TenantDetail } from '@order-pilot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { TextAreaField, TextField } from '@/components/Form';
import { IconBranch, IconPlus } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState } from '@/components/States';
import { createBranch, tenantKeys } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';

/**
 * [readOnly]: an ISSUER login lists branches (to add a till to one) but cannot
 * add them — the owner does.
 */
export function BranchesSection({
  tenant,
  readOnly = false,
}: {
  tenant: TenantDetail;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: () => createBranch(tenant.id, { name: name.trim(), address: address.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      setOpen(false);
    },
  });

  const { reset: resetMutation } = mutation;

  const openModal = useCallback(() => {
    setName('');
    setAddress('');
    setNameError(undefined);
    resetMutation();
    setOpen(true);
  }, [resetMutation]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!name.trim()) {
        setNameError('Branch name is required.');
        return;
      }
      setNameError(undefined);
      mutation.mutate();
    },
    [mutation, name],
  );

  const deviceCountFor = (branchId: string): number =>
    tenant.devices.filter((device) => device.branchId === branchId).length;

  return (
    <>
      <SectionCard
        headingId="section-branches"
        title="Branches"
        count={tenant.branches.length}
        description="Every device is bound to a branch."
        action={
          readOnly ? undefined : (
            <Button size="sm" icon={<IconPlus className="h-3.5 w-3.5" />} onClick={openModal}>
              Add branch
            </Button>
          )
        }
      >
        {tenant.branches.length === 0 ? (
          <EmptyState
            compact
            icon={<IconBranch className="h-5 w-5" />}
            title="No branches"
            description="Add a branch before registering a POS device."
            action={
              readOnly ? undefined : (
                <Button icon={<IconPlus className="h-4 w-4" />} onClick={openModal}>
                  Add branch
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th">
                    Branch
                  </th>
                  <th scope="col" className="th">
                    Address
                  </th>
                  <th scope="col" className="th">
                    Devices
                  </th>
                  <th scope="col" className="th">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenant.branches.map((branch) => (
                  <tr key={branch.id}>
                    <td className="td font-medium text-slate-900">{branch.name}</td>
                    <td className="td max-w-xs truncate text-slate-500" title={branch.address || undefined}>
                      {branch.address || '—'}
                    </td>
                    <td className="td tabular-nums">{deviceCountFor(branch.id)}</td>
                    <td className="td whitespace-nowrap text-slate-500">{formatDate(branch.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <Modal
        open={open}
        title="Add branch"
        description={`New branch for ${tenant.name}.`}
        size="sm"
        onClose={mutation.isPending ? () => undefined : () => setOpen(false)}
      >
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {mutation.isError ? <Alert tone="error">{getErrorMessage(mutation.error)}</Alert> : null}

          <TextField
            id="branch-name"
            label="Branch name"
            required
            data-autofocus
            placeholder="Main"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNameError(undefined);
            }}
            error={nameError}
            disabled={mutation.isPending}
          />

          <TextAreaField
            id="branch-address"
            label="Address"
            placeholder="Optional"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            disabled={mutation.isPending}
          />

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button onClick={() => setOpen(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              Add branch
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
